import React from 'react';
import moment from 'moment';
import { NumericFormat } from 'react-number-format';
import DetailSection from './DetailSection';
import AnnotationItem from './AnnotationItem';
import ActionsOptions from './ActionsOptions/ActionsOptions';
import { UserIcon, CurrencyEuroIcon, BookmarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import ExpedientNotesEditor from '../../../components/ExpedientNotesEditor';
import { calculatePendingBalance } from '../utils/expedientUtils';
import { DEBT_ACTIVITY_LABELS, DEBT_POSITIVE_TYPES } from '@claimup/shared/constants/expedient';

const ExpedientDetailsPanel = ({ expedient }) => {
  if (!expedient) {
    return (
      <div className='w-full flex items-center justify-center bg-white border border-slate-200 rounded-lg h-full min-h-[calc(100vh-205px)] shadow-lg'>
        <div className='text-center'>
          <p className='text-slate-500 text-sm'>Selecciona un expediente para ver los detalles</p>
        </div>
      </div>
    );
  }

  const formattedDate = expedient?.createdAt ? moment(expedient.createdAt).format('DD/MM/YYYY') : 'No Date';

  const debtor = expedient.deudores && expedient.deudores.length > 0 ? expedient.deudores[0] : null;
  const saldoPendiente = calculatePendingBalance(expedient);

  // Create transactions array from debt_activity
  const YELLOW_TYPES = ['bond', 'credit', 'cancellation'];
  const transacciones = (expedient.debt_activity || [])
    .map(item => ({
      fecha: item.date || item.createdAt || new Date(),
      concepto: item.title || DEBT_ACTIVITY_LABELS[item.type] || 'Sin concepto',
      descripcion: item.description,
      importe: item.amount || 0,
      tipo: item.type,
      isPositive: DEBT_POSITIVE_TYPES.includes(item.type),
      isYellow: YELLOW_TYPES.includes(item.type),
    }))
    .sort((a, b) => {
      const dateA = new Date(a.fecha).getTime();
      const dateB = new Date(b.fecha).getTime();
      return dateB - dateA;
    });

  return (
    <div className='w-full max-h-[calc(100vh-220px)] flex flex-col bg-white border border-slate-200 rounded-lg overflow-hidden shadow-lg'>
      {/* Header */}
      <div className='px-4 py-3 border-b border-slate-200 bg-slate-50'>
        <div className='flex items-center justify-between'>
          <h3 className='text-sm font-semibold text-slate-900'>Detalles del expediente</h3>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className='flex-1 overflow-y-auto p-4 space-y-4'>
        {/* Expedient Info */}
        <div className='space-y-2'>
          <div className='flex items-baseline gap-2'>
            <h4 className='text-xs font-semibold text-slate-500 uppercase'>Fecha Creación</h4>
            <p className='text-sm text-slate-900'>{formattedDate}</p>
          </div>
          <div className='flex items-baseline gap-2'>
            <h4 className='text-xs font-semibold text-slate-500 uppercase'>Referencia</h4>
            <p className='text-sm text-slate-900 font-mono'>{expedient.expedient_id}</p>
          </div>
          <div className='flex items-baseline gap-2'>
            <h4 className='text-xs font-semibold text-slate-500 uppercase'>Cliente</h4>
            <p className='text-sm text-slate-900'>{expedient.nombre_comercial || 'N/A'}</p>
          </div>
        </div>

        <div className='h-px bg-slate-200 my-4'></div>

        {/* Debtor Info */}
        {debtor && (
          <DetailSection title='Deudor' icon={UserIcon}>
            <div className='space-y-2 text-sm'>
              <div className='flex items-baseline gap-2'>
                <span className='text-slate-500 uppercase text-xs'>Nombre:</span>
                <p className='text-slate-900 font-medium'>{debtor.debtorname}</p>
              </div>
              {debtor.debtoremail && (
                <div className='flex items-baseline gap-2'>
                  <span className='text-slate-500 uppercase text-xs'>Email:</span>
                  <p className='text-slate-900 break-all'>{debtor.debtoremail}</p>
                </div>
              )}
              {debtor.debtorphone && (
                <div className='flex items-baseline gap-2'>
                  <span className='text-slate-500 uppercase text-xs'>Teléfono:</span>
                  <p className='text-slate-900'>{debtor.debtorphone}</p>
                </div>
              )}
              {debtor.debtoraddress && (
                <div className='flex items-baseline gap-2'>
                  <span className='text-slate-500 uppercase text-xs'>Dirección:</span>
                  <p className='text-slate-900'>{debtor.debtoraddress}</p>
                </div>
              )}
            </div>
          </DetailSection>
        )}

        <div className='h-px bg-slate-200 my-4'></div>

        {/* Debt Details */}
        <DetailSection
          title={
            <div className='flex items-center justify-between w-full'>
              <span>Detalle deuda</span>
              <NumericFormat
                value={saldoPendiente}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                suffix={' €'}
                decimalScale={2}
                renderText={value => (
                  <span className={`ml-2 text-xs ${saldoPendiente === 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Saldo: <span className='font-bold'>{value}</span>
                  </span>
                )}
              />
            </div>
          }
          icon={CurrencyEuroIcon}
        >
          {transacciones.length === 0 ? (
            <p className='text-sm text-gray-500 text-center'>No hay actividad de deuda registrada.</p>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-xs'>
                <thead>
                  <tr className='border-b border-gray-200'>
                    <th className='text-left py-2 text-gray-600 font-semibold'>Fecha</th>
                    <th className='text-left py-2 text-gray-600 font-semibold'>Tipo</th>
                    <th className='text-left py-2 text-gray-600 font-semibold'>Concepto</th>
                    <th className='text-right py-2 text-gray-600 font-semibold'>Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {transacciones.slice(0, 10).map((transaccion, index) => (
                    <tr key={index} className='border-b border-gray-100'>
                      <td className='py-2 text-gray-500'>{moment(transaccion.fecha).format('DD/MM/YYYY')}</td>
                      <td className='py-2'>
                        <span
                          className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            transaccion.isPositive
                              ? 'bg-red-100 text-red-700'
                              : transaccion.isYellow
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {DEBT_ACTIVITY_LABELS[transaccion.tipo] || transaccion.tipo}
                        </span>
                      </td>
                      <td className='py-2 text-gray-700'>
                        <div>{transaccion.concepto}</div>
                        {transaccion.descripcion && (
                          <div className='text-[10px] text-gray-400'>{transaccion.descripcion}</div>
                        )}
                      </td>
                      <td className='py-2 text-right'>
                        <NumericFormat
                          value={Math.abs(transaccion.importe)}
                          displayType={'text'}
                          thousandSeparator={'.'}
                          decimalSeparator={','}
                          suffix={' €'}
                          decimalScale={2}
                          renderText={value => (
                            <span
                              className={`font-medium ${
                                transaccion.isPositive
                                  ? 'text-red-600'
                                  : transaccion.isYellow
                                    ? 'text-yellow-600'
                                    : 'text-green-600'
                              }`}
                            >
                              {transaccion.isPositive ? '+' : '-'} {value}
                            </span>
                          )}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {transacciones.length > 10 && (
                <p className='text-xs text-slate-500 text-center mt-2'>
                  Mostrando 10 de {transacciones.length} transacciones
                </p>
              )}
            </div>
          )}
        </DetailSection>

        <div className='h-px bg-slate-200 my-4'></div>

        {/* Annotations */}
        <DetailSection title='Anotaciones' icon={BookmarkIcon}>
          {expedient.annotations && expedient.annotations.length > 0 ? (
            <div>
              {expedient.annotations
                .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
                .slice(0, 5)
                .map(annotation => (
                  <AnnotationItem key={annotation._id} annotation={annotation} />
                ))}
            </div>
          ) : (
            <p className='text-xs text-gray-500'>No hay anotaciones.</p>
          )}
        </DetailSection>

        <div className='h-px bg-slate-200 my-4'></div>

        {/* Notes */}
        <DetailSection title='Notas (interno)' icon={DocumentTextIcon}>
          <ExpedientNotesEditor
            expedientId={expedient.expedient_id || expedient._id}
            initialNotes={expedient.notes || ''}
          />
        </DetailSection>
      </div>

      {/* Actions Footer - Always Visible */}
      <div className='border-t border-slate-200 bg-slate-50 p-2'>
        <ActionsOptions expedientId={expedient.expedient_id} debtorId={debtor ? debtor._id : null} expedientType={expedient.type} />
      </div>
    </div>
  );
};

export default ExpedientDetailsPanel;
